import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { colors, type as textType, viz } from "../../styles";
import { formatCompactNumber } from "../../format";
import { SeriesDef } from "../ChartCard";
import ChartTooltip from "../ChartTooltip";

/**
 * Stacked columns. Thin marks (≤24px), a 2px surface gap between stacked
 * segments so adjacent fills never touch, rounded top corners on the topmost
 * segment only, recessive gridlines, per-column hover tooltip.
 */
const StackedColumns = <T extends Record<string, any>>({
  data,
  series,
  categoryKey,
  height = 220,
  tickFormatter,
  labelFormatter,
  valueTickFormatter = formatCompactNumber,
}: {
  data: T[];
  series: SeriesDef[];
  categoryKey: string;
  height?: number;
  tickFormatter?: (value: string) => string;
  labelFormatter?: (value: string) => string;
  /**
   * Formats the VALUE (Y) axis. Defaults to compact numbers.
   *
   * A chart plotting MONEY must pass `formatCentsCompact`: every money figure
   * this dashboard stores is in cents, so a raw axis reads a hundred times too
   * large — the customer revenue chart's "800000" meant $8,000.
   */
  valueTickFormatter?: (value: number) => string;
}) => (
  <ResponsiveContainer width="100%" height={height}>
    <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} barCategoryGap="24%">
      <CartesianGrid stroke={viz.gridline} strokeDasharray="0" vertical={false} />
      <XAxis
        dataKey={categoryKey}
        tickFormatter={tickFormatter}
        tick={{ fill: viz.axisInk, fontSize: 11 }}
        tickLine={false}
        axisLine={{ stroke: viz.axis }}
        interval="preserveStartEnd"
        minTickGap={16}
      />
      <YAxis
        tick={{ fill: viz.axisInk, fontSize: 11 }}
        tickLine={false}
        axisLine={false}
        width={44}
        allowDecimals={false}
        // Abbreviated, because 44px does not fit six digits and Recharts does
        // not shrink or ellipsize — it just paints the label clipped. `200000`
        // came out as `00000` and `120000` as `20000`: not obviously broken,
        // just quietly wrong by an order of magnitude. Exact values live in the
        // tooltip and the Table toggle.
        tickFormatter={valueTickFormatter}
      />
      <Tooltip
        cursor={{ fill: colors.surfaceMuted }}
        content={<ChartTooltip labelFormatter={labelFormatter} />}
      />
      {series.map((item, index) => (
        <Bar
          key={item.key}
          dataKey={item.key}
          name={item.label}
          stackId="stack"
          fill={item.color}
          maxBarSize={24}
          // 2px of surface between segments, and rounded ends only on the
          // topmost series so the stack reads as one column.
          stroke={colors.surface}
          strokeWidth={2}
          radius={index === series.length - 1 ? [4, 4, 0, 0] : undefined}
        />
      ))}
    </BarChart>
  </ResponsiveContainer>
);

export const EmptyChartNote = ({ label }: { label: string }) => (
  <div
    style={{
      ...textType.bodySmallRegular,
      color: colors.inkFaint,
      padding: "28px 0",
      textAlign: "center",
    }}
  >
    {label}
  </div>
);

export default StackedColumns;
